/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Context as HonoContext } from 'hono'

/**
 * Default key generator for Hono.
 * Checks X-Forwarded-For, X-Real-IP, and CF-Connecting-IP (Cloudflare Workers).
 */
export const extractIp = (c: HonoContext): string =>
  c.req.header('x-forwarded-for')?.split(',')[0]?.trim() ??
  c.req.header('x-real-ip') ??
  c.req.header('cf-connecting-ip') ??
  'unknown'

/**
 * User-scoped key: `user:<id>` when a user object is stored in context variables
 * via `c.set('user', { id: '...' })`, otherwise falls back to `ip:<address>`.
 *
 * @example
 * ```ts
 * // Auth middleware sets the user
 * app.use('/api/*', async (c, next) => {
 *   const user = await verifyToken(c.req.header('authorization'))
 *   c.set('user', user)
 *   await next()
 * })
 *
 * // Rate limiter uses user key
 * app.use('/api/*', createUserRateLimiter({ maxRequests: 200 }))
 * ```
 */
export const buildUserKey = (c: HonoContext): string => {
  const user = c.get('user') as Record<string, unknown> | undefined
  const userId: unknown = user?.['id'] ?? user?.['_id']
  if (userId != null) return `user:${String(userId)}`
  return `ip:${extractIp(c)}`
}
